/**
 * The member area's shared look (Plan Q, 28/09/2026): every field, label,
 * button, card and dropdown uses these, so a screen cannot drift into its own
 * sizes. Class names stay literal strings here, which is what lets Tailwind's
 * scanner see them.
 *
 * Sizes: fields and buttons 42 to 44px tall, text 14px (16px in fields on
 * phones, see index.css), labels 12px. Checkboxes and radios are drawn in
 * index.css, not here.
 */

/** A field's look without its width: for a field sized on purpose (w-40, w-44 …). */
export const fieldBox =
  'rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition-shadow placeholder:text-slate-400 focus:border-brand-600 focus:ring-1 focus:ring-brand-600 dark:focus:border-brand-400 dark:focus:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-navy-600 dark:bg-navy-900 dark:text-slate-100 dark:placeholder:text-slate-500';

/** Text input, select and textarea, full width. */
export const field = `block w-full ${fieldBox}`;

/** A field's label, above it. */
export const label = 'mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300';

/**
 * The sign-in screen's larger form (Plan BG, as drawn): 46px fields with 15px text, 13px
 * labels, a 48px button. Only the sign-in, code and new-password forms use these.
 */
export const auth = {
  field:
    'block h-[46px] w-full rounded-xl border border-slate-300 bg-white px-3.5 text-[15px] text-slate-900 shadow-sm outline-none transition-shadow placeholder:text-slate-400 focus:border-brand-600 focus:ring-1 focus:ring-brand-600 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-600 aria-[invalid=true]:border-2 aria-[invalid=true]:border-rose-600 dark:border-navy-600 dark:bg-navy-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-brand-400 dark:focus:ring-brand-400 dark:disabled:bg-navy-900 dark:disabled:text-slate-400 dark:aria-[invalid=true]:border-rose-400',
  label: 'block text-[13px] font-semibold text-slate-700 dark:text-slate-300',
  link: '-my-2 py-2 text-[13px] font-semibold text-brand-700 hover:text-brand-800 hover:underline dark:text-brand-300 dark:hover:text-brand-200',
  button:
    'inline-flex h-12 w-full items-center justify-center gap-2.5 rounded-xl bg-brand-700 text-[15px] font-bold text-white shadow-[0_8px_20px_-8px_rgba(3,105,161,0.6)] transition-colors hover:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-navy-950 dark:disabled:bg-navy-800 dark:disabled:text-slate-400',
  /** The button while its request runs: the brand colour kept, a spinner and "Đang …". */
  busy: 'disabled:!bg-brand-700 disabled:!text-white disabled:opacity-90 disabled:shadow-none',
} as const;

/** Small uppercase heading inside a card ("THÔNG TIN BILL"). */
export const sectionTitle = 'text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400';

/** A white panel on the page. */
export const card = 'rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5 dark:border-navy-700 dark:bg-navy-800';

const buttonBase =
  'inline-flex items-center justify-center gap-2 rounded-xl font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-60';

/** Buttons: 44px ("md") for actions, 36px ("sm") inside lists and toolbars, 44px on a phone (Apple HIG; UX audit, 17/10/2026). */
export const button = {
  primary: `${buttonBase} min-h-11 px-4 text-sm bg-brand-700 text-white hover:bg-brand-800`,
  secondary: `${buttonBase} min-h-11 px-4 text-sm bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-navy-700 dark:text-slate-200 dark:hover:bg-navy-600`,
  danger: `${buttonBase} min-h-11 px-4 text-sm bg-rose-600 text-white hover:bg-rose-700`,
  /** A destructive action that is not the main one on the screen (delete at the foot of an editor). */
  dangerSoft: `${buttonBase} min-h-11 px-4 text-sm bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-400 dark:hover:bg-rose-500/20`,
  /** Money that has actually moved: paid back, received. */
  success: `${buttonBase} min-h-11 px-4 text-sm bg-emerald-700 text-white hover:bg-emerald-800`,
  primarySm: `${buttonBase} min-h-11 px-3 sm:min-h-9 text-sm bg-brand-700 text-white hover:bg-brand-800`,
  secondarySm: `${buttonBase} min-h-11 px-3 sm:min-h-9 text-sm bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-navy-700 dark:text-slate-200 dark:hover:bg-navy-600`,
} as const;

/** A 36px icon-only button (edit, delete, close). Give it an aria-label. */
export const iconButton =
  'grid h-11 w-11 shrink-0 place-items-center rounded-lg sm:h-9 sm:w-9 text-slate-500 dark:text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 dark:hover:bg-navy-700 dark:hover:text-slate-200';

/** A dropdown panel we draw ourselves (pickers, menus). */
export const popover = 'rounded-2xl border border-slate-100 bg-white p-1.5 shadow-xl dark:border-navy-700 dark:bg-navy-800';

/** One row in a popover. Add the active/selected colours per state. */
export const menuItem =
  'flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-navy-700';

/**
 * How wide a screen's content runs (Plan AW, 01/10/2026): one of three, so the
 * screens line up and a wide monitor is used without stretching text.
 * - read: a document, a profile, a one-column form (768px: lines stay readable).
 * - manage: lists, tables, settings, statistics, approvals, mail (1024px on a
 *   1366 laptop, 1152px from 1280 wide, 1280px from 1800 wide).
 * - article: a document beside its outline (Tài liệu, 1056px as drawn in Plan BF): the text
 *   column stays readable with the outline at its right.
 * - feed: a list read from top to bottom, one item under another (Thông báo, Gen; 848px, as
 *   drawn in Plan BF): wider than a document, since each line carries a time or an action.
 * - work: a workspace that wants every pixel (the board, editors), up to the shell's own limit.
 */
export const page = {
  read: 'mx-auto w-full max-w-3xl',
  feed: 'mx-auto w-full max-w-[53rem]',
  article: 'mx-auto w-full max-w-[66rem]',
  manage: 'mx-auto w-full max-w-5xl xl:max-w-6xl min-[1800px]:max-w-7xl',
  work: 'mx-auto w-full',
} as const;

/** The space between a screen's blocks: the same everywhere. */
export const stack = 'space-y-4 sm:space-y-5';
